'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { FormLabel, InputSelect } from '~/components/primitives';
import { HoverTooltip } from './HoverTooltip';
import { unpinnedLocationStatement } from '../utils/locationStatement';
import { useI18n } from '~/features/i18n';
import { setWidgetFixedAddress, setWidgetMode, useLocationWidgetStore } from '../store';
import { venueCity } from '../utils/venueCity';
import { useCustomLocationLabel } from '~/features/activities/useCustomLocationLabel';
import { LOCATION_CUSTOM_VALUE } from '~/features/cart/cartItemLocation';
type Props = {
  /** The activity's fixed locations (partner venues). */
  addresses: ReadonlyArray<{
    id: string | number;
    name?: string | null;
  }>;
  /** Whether "Locatie naar keuze" (partner comes to you) is available. */
  comesToYou: boolean;
  /** The location the booking card will submit; null shows "Kies je locatie". */
  value: typeof LOCATION_CUSTOM_VALUE | number | null;
  className?: string;
};

/**
 * Right-panel location selector, styled to match the Variant/Taal fields in the
 * booking card. "Locatie naar keuze" drives the widget into custom mode (partner
 * comes to me); a fixed location drives it into fixed mode, through the widget store
 * the map reads too. It shows `value`, the location `QuotationCard` will submit.
 */
export const LocationDropdown: React.FC<Props> = t0 => {
  const $ = _c(58);
  const {
    addresses,
    comesToYou,
    value,
    className
  } = t0;
  const i18n = useI18n();
  const customLocationLabel = useCustomLocationLabel();
  const {
    venueCities
  } = useLocationWidgetStore();
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== addresses || $[1] !== className || $[2] !== comesToYou || $[3] !== customLocationLabel || $[4] !== i18n || $[5] !== value || $[6] !== venueCities) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t10;
      if ($[17] !== i18n || $[18] !== venueCities) {
        t10 = (address, index) => ({
          value: String(address.id),
          label: venueCities[String(address.id)] ?? venueCity(address) ?? i18n._("pdp.location.dropdown.location_n", {
            n: index + 1
          }, {
            message: "Location {n}"
          })
        });
        $[17] = i18n;
        $[18] = venueCities;
        $[19] = t10;
      } else {
        t10 = $[19];
      }
      const addressOptions = addresses.map(t10);
      if (!comesToYou && addresses.length <= 1) {
        let t11;
        if ($[20] !== className) {
          t11 = cx("flex flex-row items-center gap-5", className);
          $[20] = className;
          $[21] = t11;
        } else {
          t11 = $[21];
        }
        let t12;
        if ($[22] !== i18n) {
          t12 = i18n._("pdp.location.dropdown.label", undefined, {
            message: "Location"
          });
          $[22] = i18n;
          $[23] = t12;
        } else {
          t12 = $[23];
        }
        let t13;
        if ($[24] !== t12) {
          t13 = <FormLabel htmlFor="pdp-location-dropdown" size="md" className="w-25">{t12}</FormLabel>;
          $[24] = t12;
          $[25] = t13;
        } else {
          t13 = $[25];
        }
        let t14;
        if ($[26] !== addresses.length || $[27] !== i18n) {
          t14 = addresses.length === 0 ? unpinnedLocationStatement(i18n) : i18n._("pdp.location.dropdown.fixed_tooltip", undefined, {
            message: "This activity takes place at the provider's location."
          });
          $[26] = addresses.length;
          $[27] = i18n;
          $[28] = t14;
        } else {
          t14 = $[28];
        }
        let t15;
        if ($[29] !== i18n) {
          t15 = i18n._("pdp.location.dropdown.placeholder", undefined, {
            message: "Choose your location"
          });
          $[29] = i18n;
          $[30] = t15;
        } else {
          t15 = $[30];
        }
        const t16 = <HoverTooltip as="div" triggerClassName="w-full" content={t14}><InputSelect id="pdp-location-dropdown" className="w-full" compact={true} disabled={true} value={addressOptions[0]?.value ?? ""} placeholder={t15} options={addressOptions} onChange={_temp} /></HoverTooltip>;
        let t17;
        if ($[31] !== t11 || $[32] !== t13 || $[33] !== t16) {
          t17 = <div className={t11}>{t13}{t16}</div>;
          $[31] = t11;
          $[32] = t13;
          $[33] = t16;
          $[34] = t17;
        } else {
          t17 = $[34];
        }
        t9 = t17;
        break bb0;
      }
      if ($[35] !== className) {
        t7 = cx("flex flex-row items-center gap-5", className);
        $[35] = className;
        $[36] = t7;
      } else {
        t7 = $[36];
      }
      let t11;
      if ($[37] !== i18n) {
        t11 = i18n._("pdp.location.dropdown.label", undefined, {
          message: "Location"
        });
        $[37] = i18n;
        $[38] = t11;
      } else {
        t11 = $[38];
      }
      if ($[39] !== t11) {
        t8 = <FormLabel htmlFor="pdp-location-dropdown" size="md" className="w-25">{t11}</FormLabel>;
        $[39] = t11;
        $[40] = t8;
      } else {
        t8 = $[40];
      }
      T0 = InputSelect;
      t1 = "pdp-location-dropdown";
      t2 = "w-full";
      t3 = true;
      t4 = value == null ? "" : String(value);
      if ($[41] !== i18n) {
        t5 = i18n._("pdp.location.dropdown.placeholder", undefined, {
          message: "Choose your location"
        });
        $[41] = i18n;
        $[42] = t5;
      } else {
        t5 = $[42];
      }
      let t12;
      if ($[43] !== comesToYou || $[44] !== customLocationLabel) {
        t12 = comesToYou ? [{
          value: LOCATION_CUSTOM_VALUE,
          label: customLocationLabel
        }] : [];
        $[43] = comesToYou;
        $[44] = customLocationLabel;
        $[45] = t12;
      } else {
        t12 = $[45];
      }
      t6 = [...addressOptions, ...t12];
    }
    $[0] = addresses;
    $[1] = className;
    $[2] = comesToYou;
    $[3] = customLocationLabel;
    $[4] = i18n;
    $[5] = value;
    $[6] = venueCities;
    $[7] = T0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    T0 = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[46] !== T0 || $[47] !== t1 || $[48] !== t2 || $[49] !== t3 || $[50] !== t4 || $[51] !== t5 || $[52] !== t6) {
    t10 = <T0 id={t1} className={t2} compact={t3} value={t4} placeholder={t5} options={t6} onChange={_temp2} />;
    $[46] = T0;
    $[47] = t1;
    $[48] = t2;
    $[49] = t3;
    $[50] = t4;
    $[51] = t5;
    $[52] = t6;
    $[53] = t10;
  } else {
    t10 = $[53];
  }
  let t11;
  if ($[54] !== t10 || $[55] !== t7 || $[56] !== t8) {
    t11 = <div className={t7}>{t8}{t10}</div>;
    $[54] = t10;
    $[55] = t7;
    $[56] = t8;
    $[57] = t11;
  } else {
    t11 = $[57];
  }
  return t11;
};
function _temp() {}
function _temp2(next) {
  if (next === LOCATION_CUSTOM_VALUE) {
    setWidgetMode("custom");
    return;
  }
  setWidgetFixedAddress(String(next));
}